
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AudioLines, Clock, FileText, Film, Play, TriangleAlert } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import { Button, Card, CardBody, Seg, Skeleton } from '../../components/ui';
import { useCurrentUser, useToolTask } from '../../hooks';
import type { PlacementProposalRequest } from '../../types/api';
import { isTaskFinished } from '../../types/api';
import { DensityStep } from './DensityStep';
import { EMPTY_DENSITY, densityFormOf, densityPayload, type DensityForm } from './density';
import { ProposalResult } from './ProposalResult';
import { RunProgress } from './RunProgress';
import { ScriptStep } from './ScriptStep';
import { AudioMode } from './AudioMode';
import { SHOW_VIDEO_MODE } from './flags';
import { VideoMode } from './VideoMode';
import { MAX_TEXT_CHARS, contextPayload } from './format';
import { parseProposal, type PlacementProposalView } from './types';
import { useCreateProposal, useProposal } from './usePlacement';
import { useProposalView } from './useProposalView';
import styles from './placement.module.css';

const DRAFT_KEY = 'placement.script.draft';
const CONTEXT_DRAFT_KEY = 'placement.context.draft';

function loadDraft(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function useDraft(key: string): [string, (value: string) => void] {
  const [value, setValue] = useState(() => loadDraft(key));
  useEffect(() => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      return;
    }
  }, [key, value]);
  return [value, setValue];
}

type Mode = 'text' | 'audio' | 'video';

export function PlacementPage() {
  const [params, setParams] = useSearchParams();
  const rawMode = params.get('mode');
  const wanted: Mode = rawMode === 'video' ? 'video' : rawMode === 'audio' ? 'audio' : 'text';
  const mode: Mode = wanted === 'video' && !SHOW_VIDEO_MODE ? 'text' : wanted;

  const { can } = useCurrentUser();
  const canRun = can('editor');

  const switchMode = useCallback(
    (next: Mode) => {
      const search = new URLSearchParams();
      if (next !== 'text') search.set('mode', next);
      setParams(search, { replace: false });
    },
    [setParams],
  );

  return (
    <div className={styles.page}>
      <div className={styles.modeBar}>
        <Seg<Mode>
          ariaLabel="Что у вас на руках"
          value={mode}
          onChange={switchMode}
          options={[
            {
              value: 'text',
              label: (
                <span className={styles.modeOption}>
                  <FileText size={13} strokeWidth={1.6} /> Есть текст
                </span>
              ),
            },
            {
              value: 'audio',
              label: (
                <span className={styles.modeOption}>
                  <AudioLines size={13} strokeWidth={1.6} /> Есть озвучка
                </span>
              ),
            },
            ...(SHOW_VIDEO_MODE
              ? [
                  {
                    value: 'video' as Mode,
                    label: (
                      <span className={styles.modeOption}>
                        <Film size={13} strokeWidth={1.6} /> Есть видео
                      </span>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </div>

      {mode === 'text' && <TextMode canRun={canRun} />}
      {mode === 'audio' && <AudioMode canRun={canRun} />}
      {SHOW_VIDEO_MODE && mode === 'video' && <VideoMode canRun={canRun} />}
    </div>
  );
}

function TextMode({ canRun }: { canRun: boolean }) {
  const [params, setParams] = useSearchParams();
  const taskId = params.get('task') ?? undefined;
  const proposalIdParam = params.get('proposal') ?? undefined;

  const [text, setText] = useDraft(DRAFT_KEY);
  const [context, setContext] = useDraft(CONTEXT_DRAFT_KEY);
  const [language, setLanguage] = useState('ru');
  const [density, setDensity] = useState<DensityForm>(EMPTY_DENSITY);
  const [showForm, setShowForm] = useState(false);

  const create = useCreateProposal();
  const task = useToolTask(taskId);

  const taskProposal = useMemo(
    () => (task.data?.status === 'succeeded' ? parseProposal(task.data.result) : null),
    [task.data],
  );

  const syncedId = useRef<string | null>(null);
  useEffect(() => {
    if (!taskProposal) return;
    if (syncedId.current === taskProposal.proposalId) return;
    syncedId.current = taskProposal.proposalId;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('proposal', taskProposal.proposalId);
        return next;
      },
      { replace: true },
    );
  }, [taskProposal, setParams]);

  const remote = useProposal(taskProposal ? undefined : proposalIdParam);
  const proposal: PlacementProposalView | null = taskProposal ?? remote.data ?? null;
  const view = useProposalView(proposal);

  const isRunning = Boolean(taskId) && !isTaskFinished(task.data?.status) && !task.isError;
  const busy = isRunning || create.isPending;

  const startRun = useCallback(() => {
    const payload: PlacementProposalRequest = {
      text,
      context: contextPayload(context),
      language,
      density: densityPayload(density),
      alternatives: Number(density.alternatives) || 0,
      allow_media_reuse: density.allowMediaReuse,
    };
    create.mutate(payload, {
      onSuccess: (started) => {
        syncedId.current = null;
        view.reset();
        setShowForm(false);
        setParams({ task: started.id }, { replace: false });
      },
    });
  }, [context, create, density, language, setParams, text, view]);

  const textTooLong = text.length > MAX_TEXT_CHARS || context.length > MAX_TEXT_CHARS;
  const canStart = canRun && text.trim().length > 0 && !textTooLong && !busy;
  const formVisible = !proposal || showForm || busy;

  return (
    <>
      {create.isError && <ErrorBanner error={create.error} />}

      {task.isError && taskId && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          <TriangleAlert size={15} className={styles.bannerIconWarn} />
          <span className={styles.bannerText}>
            Статус подбора не читается. Если подбор шёл, запустите его заново.
          </span>
        </div>
      )}

      {task.data?.status === 'failed' && (
        <div className={`${styles.banner} ${styles.bannerDanger}`}>
          <TriangleAlert size={15} className={styles.bannerIconDanger} />
          <span className={styles.bannerText}>
            Подбор не удался: {task.data.error ?? 'причина неизвестна'}
          </span>
        </div>
      )}

      {busy && <RunProgress task={task.data} />}

      {formVisible && (
        <>
          <div className={styles.formGrid}>
            <ScriptStep
              text={text}
              onText={setText}
              context={context}
              onContext={setContext}
              language={language}
              onLanguage={setLanguage}
              disabled={busy || !canRun}
            />
            <div className={styles.formColumn}>
              <DensityStep
                value={density}
                onChange={setDensity}
                disabled={busy || !canRun}
                applied={proposal?.density ?? null}
                onUseApplied={() =>
                  proposal && setDensity(densityFormOf(proposal.density, density))
                }
              />

              <Button variant="primary" onClick={startRun} disabled={!canStart}>
                {busy ? (
                  <>
                    <Clock size={13} strokeWidth={1.6} /> Подбираем…
                  </>
                ) : (
                  <>
                    <Play size={13} strokeWidth={1.6} /> Подобрать вставки
                  </>
                )}
              </Button>

              {proposal && (
                <Button onClick={() => setShowForm(false)} disabled={busy}>
                  К результату
                </Button>
              )}

              {!canRun && (
                <span className={styles.footHint}>Запуск доступен роли «редактор» и выше.</span>
              )}
            </div>
          </div>
        </>
      )}

      {remote.isLoading && !proposal && (
        <Card>
          <CardBody pad className={styles.panelBody}>
            <Skeleton width="40%" height={13} />
            <Skeleton width="85%" height={13} />
            <Skeleton width="70%" height={13} />
          </CardBody>
        </Card>
      )}

      {remote.isError && !proposal && (
        <ErrorBanner error={remote.error} onRetry={() => void remote.refetch()} />
      )}

      {proposal && !showForm && !busy && (
        <ProposalResult
          proposal={proposal}
          feed={view.feed}
          mediaOf={view.mediaOf}
          overriddenOrders={view.overriddenOrders}
          selectedOrder={view.selectedOrder}
          onSelect={view.select}
          selectedItem={view.selectedItem}
          onChoose={view.choose}
          onResetChoice={view.resetChoice}
          onRerun={() => setShowForm(true)}
          onAttachTimings={() => setShowForm(true)}
        />
      )}
    </>
  );
}
